import { yupResolver } from "@hookform/resolvers/yup";
import { Attachment, Delete } from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Stack,
  Typography,
} from "@mui/material";
import { FileGroup } from "common/FileUpload";
import CustomSimpleAccordion from "qubCommon/Accordions/CustomSimpleAccordion";
import { useEffect, useMemo, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { getFiles } from "src/apis/controllers/files";
import { FileProvider, useFileContext } from "src/context/FilesV2";
import { CssTextField, Dropdown, TextInput } from "src/qubCommon/Inputs";
import {
  deleteBundle,
  deleteFile,
  downloadFile,
  formatFiles,
  uploadFiles,
} from "src/util";
import { getExpenseEmployeeRegistryInfo } from "src/apis/controllers/expenses";
import {
  getResponseMessage,
  getResponsePayload,
  isErrorResponse,
} from "./headerPayload";
import * as yup from "yup";

const EMPLOYEE_ATTACHMENTS_PROJECT_TABLE_ID = 6;

const normalizeDigits = (value, length) =>
  value?.replace?.(/\D/g, "").slice(0, length) ?? "";

const emptyToNull = (value) => {
  const nextValue = value?.toString?.().trim?.() ?? "";
  return nextValue === "" ? null : nextValue;
};

const employeeSchema = yup.object({
  afm: yup
    .string()
    .trim()
    .required("Απαιτείται ΑΦΜ.")
    .matches(/^\d{9}$/, "Το ΑΦΜ πρέπει να έχει 9 ψηφία."),
  amka: yup
    .string()
    .trim()
    .required("Απαιτείται ΑΜΚΑ.")
    .matches(/^\d{11}$/, "Ο ΑΜΚΑ πρέπει να έχει 11 ψηφία."),
  jobTitleId: yup
    .mixed()
    .test(
      "job-title-required",
      "Απαιτείται επιλογή ιδιότητας.",
      (value) => value !== undefined && value !== null && value !== ""
    ),
  email: yup
    .string()
    .trim()
    .required("Απαιτείται email.")
    .email("Συμπληρώστε έγκυρο email."),
  comments: yup.string().nullable(),
});

const initialFormValues = {
  afm: "",
  amka: "",
  jobTitleId: "",
  email: "",
  comments: "",
};

const getInitialValues = (employee, initialAfm, initialAmka) => ({
  ...initialFormValues,
  afm: employee?.afm ?? initialAfm ?? "",
  amka: employee?.amka ?? initialAmka ?? "",
  jobTitleId: employee?.jobTitleId ?? "",
  email: employee?.email ?? "",
  comments: employee?.comments ?? "",
});

const getAttachmentRequest = (employeeId) => ({
  DataKey: "MHD_Employee",
  DataValue: null,
  RecordId: null,
  RecordGuid: employeeId ?? null,
  ProjectTableId: EMPLOYEE_ATTACHMENTS_PROJECT_TABLE_ID,
});

const getBundleIds = (fileGroups, fallbackBundleIds = []) => {
  const bundleIds = fileGroups.reduce((acc, group) => {
    const groupBundleIds =
      group.bundles
        ?.filter((bundle) => bundle?.files?.length > 0)
        .map((bundle) => bundle.id) ?? [];

    return [...acc, ...groupBundleIds];
  }, []);

  return bundleIds.length > 0 ? bundleIds : fallbackBundleIds ?? [];
};

const hasIdentityChanged = (employee, values) => {
  if (!employee?.id) return false;

  return (
    normalizeDigits(employee?.afm ?? "", 9) !== normalizeDigits(values.afm, 9) ||
    normalizeDigits(employee?.amka ?? "", 11) !==
    normalizeDigits(values.amka, 11)
  );
};

const getRegistryExistingEmployee = (registryInfo, values) => {
  const existingEmployee = registryInfo?.existingEmployee;
  if (!existingEmployee?.id) return null;

  return {
    ...existingEmployee,
    afm: registryInfo?.afm ?? values?.afm ?? existingEmployee.afm,
    amka: registryInfo?.amka ?? values?.amka ?? existingEmployee.amka,
    firstName: registryInfo?.firstName ?? existingEmployee.firstName,
    lastName: registryInfo?.lastName ?? existingEmployee.lastName,
    fullName: registryInfo?.fullName ?? existingEmployee.fullName,
  };
};

const inputSx = {
  "& .MuiInputBase-input": {
    fontSize: 15,
  },
};

const formSurfaceSx = {
  width: "100%",
  maxWidth: 720,
  mx: "auto",
};

const EmployeeFormRow = ({ label, children, minHeight = 42 }) => (
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: { xs: "1fr", sm: "36% 64%" },
      alignItems: "center",
      minHeight,
      mb: 0.75,
    }}
  >
    <Box
      sx={{
        px: 1,
        color: "#111",
        fontSize: 16,
        fontStyle: "italic",
        lineHeight: 1.1,
        textAlign: { xs: "left", sm: "right" },
      }}
    >
      {label}:
    </Box>
    <Box
      sx={{
        minWidth: 0,
        px: 1,
        py: 0.35,
        "& > *": {
          width: "100%",
        },
      }}
    >
      {children}
    </Box>
  </Box>
);

const RegistryReadOnlyField = ({ label, value }) => (
  <EmployeeFormRow label={label}>
    <CssTextField
      label={null}
      value={value || "-"}
      variant="standard"
      size="small"
      disabled
      InputProps={{ disableUnderline: true }}
      sx={inputSx}
    />
  </EmployeeFormRow>
);

const EmployeeAttachments = ({ isSaving = false }) => {
  const { fileGroups } = useFileContext();
  const hasFileGroups = fileGroups?.length > 0;

  if (!hasFileGroups) return null;

  const AccordionTitle = () => (
    <Stack direction="row" gap={1} alignItems="center">
      <Attachment color="primary" fontSize="small" sx={{ ml: "2px" }} />
      <Typography color="primary" sx={{ fontWeight: 1000 }}>
        ΒΙΟΓΡΑΦΙΚΟ
      </Typography>
    </Stack>
  );

  const AccordionContent = (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        {fileGroups.map((group) => (
          <FileGroup
            reviewMode={false}
            isReadOnly={isSaving}
            canAdd
            canDelete
            key={group.id}
            id={group.id}
            title={group?.title}
            maxSize={2097152}
            maxFiles={5}
            maxBundles={group?.maxBundles}
            fields={group.fields}
            bundles={group?.bundles}
            templates={group?.templates}
            extraTypeDataKeys={group?.extraTypeDataKeys}
          />
        ))}
      </Grid>
    </Grid>
  );

  return (
    <CustomSimpleAccordion
      title={<AccordionTitle />}
      content={AccordionContent}
    />
  );
};

const ExpenseEmployeeDialogContent = ({
  open,
  employee,
  initialAfm,
  initialAmka,
  applicationId,
  transTypeId,
  jobTypes = [],
  isSaving = false,
  isDeleting = false,
  onCancel,
  onSave,
  onDelete,
  onSelectExisting,
}) => {
  const isEditMode = !!employee?.id;
  const form = useForm({
    resolver: yupResolver(employeeSchema),
    defaultValues: initialFormValues,
  });
  const {
    formState: { errors },
    getValues,
    handleSubmit,
    reset,
    setValue,
    trigger,
    watch,
  } = form;
  const { control: filesControl, fileGroups } = useFileContext();
  const [attachmentsError, setAttachmentsError] = useState(null);
  const [isAttachmentsLoading, setIsAttachmentsLoading] = useState(false);
  const [hasLoadedAttachments, setHasLoadedAttachments] = useState(false);
  const [isFormInitialized, setIsFormInitialized] = useState(false);
  const [isUploadingFiles, setIsUploadingFiles] = useState(false);
  const [isDeleteWarningOpen, setIsDeleteWarningOpen] = useState(false);
  const [registryInfo, setRegistryInfo] = useState(null);
  const [registryError, setRegistryError] = useState(null);
  const [isRegistryLoading, setIsRegistryLoading] = useState(false);
  const afmValue = watch("afm");
  const amkaValue = watch("amka");

  const jobTypeOptions = useMemo(
    () =>
      (jobTypes ?? []).map((jobType) => ({
        ...jobType,
        value: jobType.id,
        label: jobType.name,
      })),
    [jobTypes]
  );
  const hasJobTypes = jobTypeOptions.length > 0;
  const canEdit = !isEditMode || employee?.canEdit === true;
  const canDelete = isEditMode && employee?.canDelete === true;
  const canShowCreateFields = isEditMode || registryInfo?.canCreate === true;
  const canSave =
    canEdit &&
    canShowCreateFields &&
    isFormInitialized &&
    hasLoadedAttachments &&
    !isSaving &&
    !isDeleting &&
    !isUploadingFiles &&
    !isAttachmentsLoading &&
    !isRegistryLoading &&
    hasJobTypes;

  useEffect(() => {
    if (!open) {
      setIsFormInitialized(false);
      return;
    }

    reset(getInitialValues(employee, initialAfm, initialAmka));
    setAttachmentsError(null);
    setRegistryInfo(null);
    setRegistryError(null);
    setIsRegistryLoading(false);
    setIsDeleteWarningOpen(false);
    setHasLoadedAttachments(false);
    setIsFormInitialized(true);
  }, [employee, initialAfm, initialAmka, open, reset]);

  useEffect(() => {
    if (!open) return undefined;

    let isActive = true;

    const fetchAttachments = async () => {
      setIsAttachmentsLoading(true);
      setHasLoadedAttachments(false);
      setAttachmentsError(null);

      try {
        const res = await getFiles(getAttachmentRequest(employee?.id ?? null));
        if (!isActive) return;

        filesControl.initialize(formatFiles(res?.data ?? []));
        filesControl.setIsDirty?.(false);
      } catch (error) {
        if (!isActive) return;

        filesControl.initialize([]);
        filesControl.setIsDirty?.(false);
        setAttachmentsError("Δεν φορτώθηκαν τα στοιχεία βιογραφικού.");
      } finally {
        if (isActive) {
          setIsAttachmentsLoading(false);
          setHasLoadedAttachments(true);
        }
      }
    };

    fetchAttachments();

    return () => {
      isActive = false;
    };
  }, [employee?.id, open]);

  useEffect(() => {
    if (isEditMode || !registryInfo) return;

    const currentAfm = normalizeDigits(afmValue, 9);
    const currentAmka = normalizeDigits(amkaValue, 11);
    const registryAfm = normalizeDigits(registryInfo?.afm, 9);
    const registryAmka = normalizeDigits(registryInfo?.amka, 11);

    if (currentAfm === registryAfm && currentAmka === registryAmka) return;

    setRegistryInfo(null);
    setRegistryError(null);
  }, [afmValue, amkaValue, isEditMode, registryInfo]);

  const handleRegistryLookup = async () => {
    setRegistryError(null);
    setRegistryInfo(null);

    if (!applicationId || !transTypeId) {
      setRegistryError("Δεν βρέθηκαν τα στοιχεία της ΜΗΔ ή του παραστατικού.");
      return;
    }

    const values = getValues();
    const afm = normalizeDigits(values.afm, 9);
    const amka = normalizeDigits(values.amka, 11);

    setValue("afm", afm, { shouldDirty: true, shouldValidate: true });
    setValue("amka", amka, { shouldDirty: true, shouldValidate: true });

    const isValidIdentity = await trigger(["afm", "amka"]);
    if (!isValidIdentity) return;

    try {
      setIsRegistryLoading(true);
      const res = await getExpenseEmployeeRegistryInfo({
        applicationId,
        transTypeId,
        afm,
        amka,
      });

      if (isErrorResponse(res)) {
        setRegistryError(
          getResponseMessage(res, "Δεν ολοκληρώθηκε η αναζήτηση στοιχείων.")
        );
        return;
      }

      const responsePayload = getResponsePayload(res);
      const payload = {
        ...responsePayload,
        afm: responsePayload?.afm ?? afm,
        amka: responsePayload?.amka ?? amka,
      };
      setRegistryInfo(payload);

      if (payload?.existingEmployee?.id) {
        setRegistryError(
          payload?.message ??
          "Ο εργαζόμενος υπάρχει ήδη στη ΜΗΔ. Μπορείτε να τον επιλέξετε."
        );
        return;
      }

      if (payload?.canCreate !== true) {
        setRegistryError(
          payload?.message ?? "Δεν επιτρέπεται δημιουργία εργαζομένου."
        );
      }
    } catch (error) {
      setRegistryError(
        error?.message ?? "Δεν ολοκληρώθηκε η αναζήτηση στοιχείων."
      );
    } finally {
      setIsRegistryLoading(false);
    }
  };

  const handleSelectExistingEmployee = () => {
    const employeeToSelect = getRegistryExistingEmployee(registryInfo, getValues());
    if (!employeeToSelect?.id) return;

    onSelectExisting?.(employeeToSelect);
  };

  const handleSave = async (values) => {
    setAttachmentsError(null);

    if (!applicationId || !transTypeId) {
      setAttachmentsError("Δεν βρέθηκαν τα στοιχεία της ΜΗΔ ή του παραστατικού.");
      return;
    }

    if (!isEditMode && registryInfo?.canCreate !== true) {
      setRegistryError("Αναζητήστε πρώτα τα στοιχεία του εργαζομένου.");
      return;
    }

    const identityChanged = hasIdentityChanged(employee, values);

    if (identityChanged && !filesControl.isDirty) {
      setAttachmentsError(
        "Για αλλαγή ΑΦΜ ή ΑΜΚΑ απαιτείται νέο βιογραφικό."
      );
      return;
    }

    try {
      setIsUploadingFiles(true);
      const hadFileChanges = filesControl.isDirty;

      if (hadFileChanges) {
        const uploadRes = await filesControl.uploadFileGroups();

        if (isErrorResponse(uploadRes)) {
          setAttachmentsError(
            getResponseMessage(
              uploadRes,
              "Δεν αποθηκεύτηκε το βιογραφικό."
            )
          );
          return;
        }

        filesControl.setIsDirty?.(false);
      }

      const bundleIds = getBundleIds(fileGroups, employee?.bundleIds);

      if (identityChanged && bundleIds.length === 0) {
        setAttachmentsError(
          "Για αλλαγή ΑΦΜ ή ΑΜΚΑ απαιτείται επισύναψη βιογραφικού."
        );
        return;
      }

      const payload = {
        ...(isEditMode ? { id: employee.id } : {}),
        applicationId,
        transTypeId,
        afm: normalizeDigits(values.afm, 9),
        amka: normalizeDigits(values.amka, 11),
        jobTitleId: Number(values.jobTitleId),
        email: values.email?.toString?.().trim?.() ?? "",
        comments: emptyToNull(values.comments),
        ...(hadFileChanges || !isEditMode || identityChanged
          ? { bundleIds }
          : {}),
      };

      await onSave?.(payload);
    } finally {
      setIsUploadingFiles(false);
    }
  };

  return (
    <FormProvider {...form}>
      <Dialog open={open} onClose={onCancel} fullWidth maxWidth="md">
        <DialogTitle variant="h4">
          {isEditMode ? "Επεξεργασία Εργαζομένου" : "Νέος Εργαζόμενος"}
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          {!isFormInitialized || !hasLoadedAttachments ? (
            <Alert severity="info">Φόρτωση στοιχείων εργαζομένου...</Alert>
          ) : (
            <Stack spacing={1.5}>
              {!hasJobTypes && (
                <Alert severity="error">
                  Δεν φορτώθηκαν οι επιλογές ιδιότητας εργαζομένου.
                </Alert>
              )}
              {attachmentsError && (
                <Alert severity="warning">{attachmentsError}</Alert>
              )}
              {registryError && (
                <Alert
                  severity={registryInfo?.existingEmployee?.id ? "info" : "warning"}
                  action={
                    registryInfo?.existingEmployee?.id ? (
                      <Button
                        variant='contained'
                        color="primary"
                        size="small"
                        onClick={handleSelectExistingEmployee}
                      >
                        Επιλογή
                      </Button>
                    ) : null
                  }
                >
                  {registryError}
                </Alert>
              )}
              {isAttachmentsLoading && (
                <Alert severity="info">Φόρτωση βιογραφικού...</Alert>
              )}

              <Box sx={formSurfaceSx}>
                <EmployeeFormRow label="ΑΜΚΑ">
                  <TextInput
                    name="amka"
                    label={null}
                    variant="standard"
                    rules={{ setValueAs: (value) => normalizeDigits(value, 11) }}
                    blurFn={(event) =>
                      setValue("amka", normalizeDigits(event.target.value, 11), {
                        shouldDirty: true,
                        shouldValidate: true,
                      })
                    }
                    sx={inputSx}
                  />
                </EmployeeFormRow>

                <EmployeeFormRow label="ΑΦΜ">
                  <TextInput
                    name="afm"
                    label={null}
                    variant="standard"
                    rules={{ setValueAs: (value) => normalizeDigits(value, 9) }}
                    blurFn={(event) =>
                      setValue("afm", normalizeDigits(event.target.value, 9), {
                        shouldDirty: true,
                        shouldValidate: true,
                      })
                    }
                    sx={inputSx}
                  />
                </EmployeeFormRow>

                {!isEditMode && (
                  <Box sx={{ display: "flex", justifyContent: "flex-end", px: 1 }}>
                    <Button
                      variant="contained"
                      color="secondary"
                      onClick={handleRegistryLookup}
                      disabled={isRegistryLoading || isSaving || isDeleting}
                      sx={{ color: "#fff", fontWeight: 800 }}
                    >
                      {isRegistryLoading ? "Αναζήτηση..." : "Αναζήτηση στοιχείων"}
                    </Button>
                  </Box>
                )}

                {registryInfo?.canCreate === true && (
                  <Box
                    sx={{
                      border: "1px solid rgba(0, 73, 144, 0.16)",
                      borderRadius: 1,
                      mx: 1,
                      mt: 1,
                      p: 1.25,
                    }}
                  >
                    <Typography sx={{ fontWeight: 900, mb: 0.75 }}>
                      Στοιχεία μητρώου
                    </Typography>
                    <RegistryReadOnlyField
                      label="Ονοματεπώνυμο"
                      value={
                        registryInfo.fullName ||
                        [registryInfo.firstName, registryInfo.lastName]
                          .filter(Boolean)
                          .join(" ")
                      }
                    />
                    <RegistryReadOnlyField
                      label="Πατρώνυμο"
                      value={registryInfo.fatherName}
                    />
                    <RegistryReadOnlyField
                      label="Ημ/νία γέννησης"
                      value={registryInfo.dob}
                    />
                  </Box>
                )}

                {canShowCreateFields && (
                  <>
                    <EmployeeFormRow label="Ιδιότητα">
                      <Dropdown
                        name="jobTitleId"
                        label={null}
                        options={jobTypeOptions}
                        valueName="value"
                        labelName="label"
                        placeholder="Επιλέξτε ιδιότητα"
                        disabled={!hasJobTypes}
                        sx={inputSx}
                      />
                    </EmployeeFormRow>

                    <EmployeeFormRow label="Email">
                      <TextInput
                        name="email"
                        label={null}
                        variant="standard"
                        sx={inputSx}
                      />
                    </EmployeeFormRow>

                    <EmployeeFormRow label="Σχόλια" minHeight={74}>
                      <TextInput
                        name="comments"
                        label={null}
                        multiline
                        minRows={2}
                        variant="standard"
                        sx={inputSx}
                      />
                    </EmployeeFormRow>

                    {errors?.jobTitleId?.message && (
                      <Alert severity="warning" sx={{ mt: 1 }}>
                        {errors.jobTitleId.message}
                      </Alert>
                    )}
                  </>
                )}
              </Box>

              {canShowCreateFields && (
                <Box sx={formSurfaceSx}>
                  {!isAttachmentsLoading && (
                    <EmployeeAttachments
                      isSaving={isSaving || isUploadingFiles || isDeleting}
                    />
                  )}
                </Box>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          {canDelete && (
            <Button
              variant="outlined"
              color="error"
              startIcon={<Delete />}
              onClick={() => setIsDeleteWarningOpen(true)}
              sx={{ mr: "auto" }}
              disabled={isDeleting || isSaving || isUploadingFiles}
            >
              Διαγραφή
            </Button>
          )}
          <Button variant="contained" color="error" onClick={onCancel}>
            Άκυρο
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit(handleSave)}
            disabled={!canSave}
          >
            {isEditMode ? "Αποθήκευση" : "Προσθήκη"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={isDeleteWarningOpen}
        onClose={() => setIsDeleteWarningOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle variant="h4">Διαγραφή Εργαζομένου</DialogTitle>
        <DialogContent>
          <Typography>
            Ο εργαζόμενος θα διαγραφεί οριστικά εφόσον δεν έχει χρησιμοποιηθεί
            σε γραμμή δαπάνης. Θέλετε να συνεχίσετε;
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            onClick={() => setIsDeleteWarningOpen(false)}
          >
            Άκυρο
          </Button>
          <Button
            variant="contained"
            color="error"
            disabled={isDeleting}
            onClick={() => {
              setIsDeleteWarningOpen(false);
              onDelete?.(employee);
            }}
          >
            Διαγραφή
          </Button>
        </DialogActions>
      </Dialog>
    </FormProvider>
  );
};

const ExpenseEmployeeDialog = ({ employee, ...props }) => {
  const employeeId = employee?.id ?? null;

  return (
    <FileProvider
      downloadFile={(fileId, type) => downloadFile(fileId, type)}
      uploadFiles={(fileGroups) => uploadFiles(fileGroups, null)}
      onDeleteBundle={async (bundleId) =>
        await deleteBundle(
          bundleId,
          EMPLOYEE_ATTACHMENTS_PROJECT_TABLE_ID,
          null,
          employeeId,
          null
        )
      }
      onDeleteFile={async (fileId) => {
        await deleteFile(
          fileId,
          EMPLOYEE_ATTACHMENTS_PROJECT_TABLE_ID,
          null,
          employeeId,
          null
        );
      }}
    >
      <ExpenseEmployeeDialogContent employee={employee} {...props} />
    </FileProvider>
  );
};

export default ExpenseEmployeeDialog;
